import { PlayCanvasGS3DManager } from '@/plugins/gsplat/PCManager'
import type { EngineType, GS3DManagerConfig } from '@/plugins/gsplat/types/ConfigTypes'
import type { IGS3DManager } from '@/shared/types/gsplat'

/** 引擎工厂函数类型 */
export type ManagerFactory = (config: GS3DManagerConfig) => IGS3DManager

/** 引擎工厂注册表 */
const engineFactories = new Map<EngineType, ManagerFactory>()

/**
 * 注册引擎工厂。
 *
 * @param engine - 引擎类型标识，当前支持 'playcanvas'
 * @param factory - 工厂函数，接收配置并返回 IGS3DManager
 */
export function registerEngine(engine: EngineType, factory: ManagerFactory): void {
  engineFactories.set(engine, factory)
}

/**
 * 创建 GS3DManager 实例。
 *
 * @param config - 管理器配置，含引擎类型、场景、渲染器等
 * @returns 与引擎匹配的 IGS3DManager 实现
 */
export function createGS3DManager(config: GS3DManagerConfig): IGS3DManager {
  const factory = engineFactories.get(config.engine)

  if (!factory) {
    const availableEngines = Array.from(engineFactories.keys()).join(', ')
    throw new Error(
      `Unknown engine: "${config.engine}". ` +
        `Available engines: ${availableEngines || 'none registered'}`
    )
  }

  return factory(config)
}

/**
 * 获取已注册的引擎列表。
 *
 * @returns 已注册引擎类型数组；未注册时为空数组
 */
export function getRegisteredEngines(): EngineType[] {
  return Array.from(engineFactories.keys())
}

registerEngine('playcanvas', (config) => {
  return new PlayCanvasGS3DManager(
    config.scene as import('three').Scene,
    config.renderer as import('three').WebGLRenderer,
    config.lodConfig,
    { withCredentials: config.withCredentials }
  )
})
